/**
 * Device picker: what is online, what is busy and who holds it.
 *
 * It shows the awkward states rather than hiding them — a phone that is plugged in but unusable
 * (`adb_state` unauthorized / no permissions), a hot phone, a nearly empty battery. Those are the
 * reasons a test mysteriously fails, and the registry already knows them. The serial and the machine
 * are always visible: that is how people identify a physical phone on a shelf.
 */

import { DirectionChip, useDirectionName, useDirections } from '../marketing/directions'
import type { Direction } from '../../api/types'
import { BatteryMedium, RefreshCw, Thermometer } from 'lucide-react'
import { useMemo, useState } from 'react'

import type { Device } from '../../api/types'
import { phoneName } from './phoneName'
import { useT } from '../../i18n'
import { useApp } from '../../lib/context'
import { usePoll } from '../../lib/poll'
import { Badge, Card, Chips, Empty, Pill, Skeletons, Status } from '../../ui/components'
import { PhoneIcon } from '../../ui/PhoneIcon'
import { filterDevices, machinesOf, takenFirst, type Mode } from './filter'

export function DeviceList({
  selectedId,
  currentSessionId,
  onPick,
}: {
  selectedId: string | null
  currentSessionId: string | null
  onPick: (device: Device) => void
}) {
  const dirs = useDirections()
  const { api, authEpoch } = useApp()
  const t = useT()
  const [query, setQuery] = useState('')
  const [machine, setMachine] = useState('')
  const [mode, setMode] = useState<Mode>('all')
  //: '' every phone, 'none' — not bound to a direction yet, else a direction id
  const [direction, setDirection] = useState('')
  const nameOf = useDirectionName()
  const { data, error, refresh } = usePoll(() => api.devices(), 5000, [api, authEpoch])

  const machines = useMemo(() => machinesOf(data ?? []), [data])
  const devices = useMemo(
    () => takenFirst(filterDevices(data ?? [], {
      query, machine, mode, currentSessionId, direction,
      directionName: (id) => { const d = dirs.byId.get(id); return d ? nameOf(d) : id },
    }), currentSessionId),
    [data, query, machine, mode, currentSessionId, direction, dirs.byId, nameOf],
  )
  const online = (data ?? []).filter((d) => d.status !== 'offline').length

  return (
    <Card
      title={t.devices.title}
      sub={data ? t.devices.onlineOf(online, data.length) : undefined}
      actions={
        <button className="ghost icon" onClick={refresh} title={t.devices.refresh} aria-label={t.devices.refresh}>
          <RefreshCw className="ico" size={15} aria-hidden />
        </button>
      }
    >
      <div className="row">
        <input
          className="grow"
          placeholder={t.devices.search}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label={t.devices.search}
        />
      </div>
      <div className="row">
        <select
          className="grow"
          value={machine}
          onChange={(e) => setMachine(e.target.value)}
          aria-label={t.devices.machineFilter}
          title={t.devices.machineFilter}
        >
          <option value="">{t.devices.allMachines}</option>
          {machines.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
        {/* By direction (owner, 2026-10-12): which phones post games, which dramas, which are not bound yet. */}
        <select
          className="grow"
          value={direction}
          onChange={(e) => setDirection(e.target.value)}
          aria-label={t.devices.directionFilter}
          title={t.devices.directionFilter}
        >
          <option value="">{t.devices.allDirections}</option>
          {dirs.list.map((d) => (
            <option key={d.direction_id} value={d.direction_id}>{nameOf(d)}</option>
          ))}
          <option value="none">{t.devices.noDirection}</option>
        </select>
      </div>
      <div className="row">
        <Chips
          value={mode}
          onChange={setMode}
          options={[
            { id: 'all', label: t.devices.all },
            { id: 'free', label: t.devices.free },
            { id: 'mine', label: t.devices.mine },
          ]}
        />
        <span className="grow" />
        <Status text={error ? t.devices.error(error.message) : null} bad />
      </div>

      <div className="devices" style={{ marginTop: 'var(--sp-3)' }}>
        {!data && !error && <Skeletons count={3} />}
        {data && devices.length === 0 && (
          <Empty>
            <span>{t.devices.nothing}</span>
            <span className="dim tiny">{t.devices.hint}</span>
          </Empty>
        )}
        {devices.map((d) => (
          <DeviceRow
            key={d.device_id}
            device={d}
            mine={Boolean(d.held_by_me || (d.session_id && d.session_id === currentSessionId))}
            direction={d.direction_id ? dirs.byId.get(d.direction_id) : null}
            selected={d.device_id === selectedId}
            onPick={onPick}
          />
        ))}
      </div>
    </Card>
  )
}

function DeviceRow({
  device: d,
  mine,
  selected,
  onPick,
  direction,
}: {
  device: Device
  /** the phone's marketing direction, shown by name (owner, 2026-10-12: "просто крапочка з кольором не
   *  інформативно") — only when it has one */
  direction?: Direction | null
  mine: boolean
  selected: boolean
  onPick: (d: Device) => void
}) {
  const t = useT()
  const busy = d.status === 'busy' && !mine
  //: Automatic posting holds the phone (2026-10-08): the badge names the task, not a colleague, and the
  //: title says why no one can take it.
  const posting = busy && Boolean(d.held_by_post)
  const held = d.held_for_s != null ? Math.max(1, Math.round(d.held_for_s / 60)) : null
  // "device" is the healthy value; anything else means plugged in but unusable, which otherwise looks
  // identical to a working phone until a job fails on it.
  const adbTrouble = d.adb_state && d.adb_state !== 'device' ? d.adb_state : null
  const statusTitle =
    d.status === 'offline' ? t.devices.statusOffline : posting ? t.devices.statusPosting : busy ? t.devices.statusBusy : t.devices.statusOnline

  return (
    <button
      className={`device${selected ? ' selected' : ''}`}
      onClick={() => onPick(d)}
      aria-current={selected}
    >
      <PhoneIcon status={mine ? 'online' : d.status} title={statusTitle} />
      {/* The name, its Android, then how a person
          finds the phone — the machine chip, the TikTok account on it, the serial — and the battery. */}
      <span style={{ minWidth: 0 }}>
        <span className="name">{phoneName(d)}</span>
        {/* What the phone really is, under the name people gave it — as on Machines (owner, 2026-10-12:
            "залишилась тільки назва яка може змінюватись, а реальна назва телефона — ні"): the model when a
            name stands above it, the serial, the Android version (on a line of its own: beside the name it
            was cut, 2026-10-06). */}
        <span className="os mono">
          {d.name && d.model ? `${d.model} · ` : ''}{d.serial}{d.os_version ? ` · Android ${d.os_version}` : ''}
        </span>
        <span className="meta">
          {direction && <DirectionChip direction={direction} />}
          {/* The machine is how a person finds a phone — "the one on oleg" — so it is a chip, in one
              colour that identifies and does not rate. */}
          {d.hub_id ? <span className="on-machine">{d.hub_id}</span> : null}
          {/* Which TikTok account is on this phone is how a person picks *which* phone to open. */}
          {d.tiktok_account
            ? <span className="mono account-line">@{d.tiktok_account.replace(/^@/, '')}</span>
            : <span className="dim">{t.devices.noAccount}</span>}
          {d.battery != null ? <span><BatteryMedium className="ico" size={13} aria-hidden /> {t.devices.battery(d.battery)}</span> : null}
          {(d.not_recommended ?? 0) > 0 && <span className="flag-bad">{t.devices.notRecommended(d.not_recommended!)}</span>}
          {d.temperature != null && d.temperature >= 40 ? (
            <span><Thermometer className="ico" size={13} aria-hidden /> {t.devices.hot(Math.round(d.temperature))}</span>
          ) : null}
        </span>
      </span>
      <span style={{ display: 'grid', gap: 4, justifyItems: 'end' }}>
        {mine ? (
          <Badge kind="ok">{t.devices.mySession}</Badge>
        ) : posting ? (
          // who launched it goes in the tooltip: in the badge, "auto-posting · someone@mornhouse.com" took
          // the row's width and squeezed the phone's name into a column of single letters (2026-10-14)
          <Badge kind="warn" title={d.held_by ? `${t.devices.statusPosting} · ${d.held_by}` : t.devices.statusPosting}>
            🤖 {t.devices.posting}
          </Badge>
        ) : busy ? (
          <Badge kind="warn">{held ? t.devices.busyFor(held) : t.devices.busy}</Badge>
        ) : d.status === 'offline' ? (
          <Badge>{t.devices.offline}</Badge>
        ) : (
          <Pill>{t.devices.open}</Pill>
        )}
        {adbTrouble && <Badge kind="bad">{t.devices.adbTrouble(adbTrouble)}</Badge>}
      </span>
    </button>
  )
}
